import type {
    LifecycleAction,
    LifecycleActionOperationKind,
    LifecycleActionTriggerKind,
    LifecycleInitiative,
    LifecycleTemplateId,
} from '@/shared/components/lifecycles/domain';
import type { LifecycleTaskPreview } from '@/shared/components/lifecycles/types';
import { getWorkspaceResourceDetailRoute } from '@/shared/workspaceResourceRoutes';
import type { ResourceActivityResourceType } from '@/types';
import { TaskParticipantRole, TaskStatusCategory } from '@/types';

import { apiClient } from '../http';
import { favoriteService } from './favorites';

export interface LifecycleApiTaskGroup {
    color?: string;
    description?: string;
    id: string;
    name: string;
    position: number;
    tasks?: Array<
        Omit<LifecycleTaskPreview, 'completed' | 'dueAt' | 'updatedAt'> & {
            completed?: boolean;
            dueAt?: string;
            updatedAt?: string;
        }
    >;
}

export interface LifecycleApiCategory {
    color?: string;
    description?: string;
    groups?: LifecycleApiTaskGroup[];
    id: string;
    name: string;
    position: number;
}

export interface LifecycleApiAction {
    enabled: boolean;
    id: string;
    label: string;
    nextRunAt?: string | null;
    operationConfig: Record<string, unknown>;
    operationKind: LifecycleActionOperationKind;
    revision: number;
    triggerConfig: Record<string, unknown>;
    triggerKind: LifecycleActionTriggerKind;
}

export interface LifecycleApiRecord {
    actions?: LifecycleApiAction[];
    archived: boolean;
    categories?: LifecycleApiCategory[];
    createdAt: string;
    description?: string;
    endAt?: string | null;
    favorited?: boolean;
    id: string;
    locked: boolean;
    name: string;
    revision: number;
    startAt?: string | null;
    status: string;
    subject?: { label: string; resourceId?: string; resourceType?: string };
    taskSummary?: { completedTasks: number; totalTasks: number };
    templateKey: LifecycleTemplateId;
    updatedAt: string;
    visibility: LifecycleInitiative['visibility'];
}

export interface LifecycleListPage {
    items: LifecycleApiRecord[];
    nextCursor?: string;
}

export interface CreateLifecycleInput {
    categories?: Array<{
        color?: string;
        description?: string;
        groups: Array<{
            color?: string;
            description?: string;
            name: string;
            tasks: Array<{ title: string }>;
        }>;
        name: string;
    }>;
    description?: string;
    endAt?: string;
    name: string;
    startAt?: string;
    subject?: { label: string; resourceId?: string; resourceType?: string };
    templateKey: LifecycleTemplateId;
    visibility: LifecycleInitiative['visibility'];
}

export interface UpdateLifecycleInput extends Partial<
    Omit<CreateLifecycleInput, 'categories' | 'endAt' | 'startAt' | 'templateKey'>
> {
    archived?: boolean;
    currentCategoryId?: string | null;
    locked?: boolean;
    endAt?: string | null;
    revision: number;
    startAt?: string | null;
    status?: string;
}

export const lifecycleDuplicateInput = (lifecycle: LifecycleInitiative): CreateLifecycleInput => ({
    categories: lifecycle.taskCategories.map((category) => ({
        color: category.color,
        description: category.description,
        groups: category.taskGroups.map((group) => ({
            color: group.color,
            description: group.description,
            name: group.label,
            tasks: group.tasks.map((task) => ({ title: task.title })),
        })),
        name: category.label,
    })),
    description: lifecycle.description,
    endAt: lifecycle.endAt ? new Date(lifecycle.endAt).toISOString() : undefined,
    name: `${lifecycle.label} Copy`,
    startAt: lifecycle.startAt ? new Date(lifecycle.startAt).toISOString() : undefined,
    subject: lifecycle.associatedResource
        ? {
              label: lifecycle.associatedResource.label,
              resourceId: lifecycle.associatedResource.id,
              resourceType: lifecycle.associatedResource.resourceType,
          }
        : lifecycle.subject
          ? { label: lifecycle.subject }
          : undefined,
    templateKey: lifecycle.templateId,
    visibility: lifecycle.visibility,
});

export interface UpdateLifecycleHierarchyOrderInput {
    categories: Array<{ groupIds: string[]; id: string }>;
    revision: number;
}

export interface LifecycleCollaboratorInput {
    memberId: string;
    permission: 'Comment' | 'Edit' | 'View';
    roles: Array<'Assignee' | 'Collaborator' | 'Reviewer'>;
}

export interface LifecycleCollaboratorUpdateInput extends Partial<LifecycleCollaboratorInput> {
    status?: 'Active' | 'Blocked' | 'Deactivated' | 'Pending';
}

export interface LifecycleCollaboratorRecord extends LifecycleCollaboratorInput {
    id: string;
    member?: {
        id: string;
        permission?: string;
        status?: string;
        user?: {
            avatar?: string;
            emailAddress?: string;
            firstName?: string;
            id: string;
            lastName?: string;
        };
    };
    status: 'Active' | 'Blocked' | 'Deactivated' | 'Pending';
}

export interface LifecycleActionExecutionRecord {
    completedAt?: string;
    createdAt: string;
    errorCode?: string;
    errorMessage?: string;
    id: string;
    result?: Record<string, unknown>;
    status: 'Failed' | 'Pending' | 'Running' | 'Skipped' | 'Succeeded';
}

const labelize = (value: string) =>
    value
        .split('-')
        .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
        .join(' ');

const normalizeAction = (action: LifecycleApiAction): LifecycleAction => ({
    enabled: action.enabled,
    id: action.id,
    label: action.label,
    operation: {
        description: `Run the ${labelize(action.operationKind).toLocaleLowerCase()} operation.`,
        kind: action.operationKind,
        label: labelize(action.operationKind),
    },
    revision: action.revision,
    trigger: {
        description: `Evaluate the ${labelize(action.triggerKind).toLocaleLowerCase()} trigger.`,
        kind: action.triggerKind,
        label: labelize(action.triggerKind),
    },
});

const lifecycleSubjectResourceTypes = new Set<ResourceActivityResourceType>([
    'dashboards',
    'folders',
    'investments',
    'lifecycles',
    'lists',
    'memos',
    'pipelines',
    'reports',
    'teams',
    'tasks',
]);

const lifecycleSubjectHref = (resourceType: ResourceActivityResourceType, resourceId: string) => {
    if (resourceType === 'tasks') {
        return `/relations/tasks?taskId=${encodeURIComponent(resourceId)}`;
    }
    if (resourceType === 'reports') {
        return undefined;
    }
    return getWorkspaceResourceDetailRoute(resourceType, resourceId);
};

const normalizeAssociatedResource = (subject: LifecycleApiRecord['subject']) => {
    const resourceType = subject?.resourceType as ResourceActivityResourceType | undefined;
    const resourceId = subject?.resourceId;

    if (!subject?.label || !resourceId || !resourceType || !lifecycleSubjectResourceTypes.has(resourceType)) {
        return undefined;
    }

    return {
        href: lifecycleSubjectHref(resourceType, resourceId),
        id: resourceId,
        label: subject.label,
        resourceType,
    };
};

export const normalizeLifecycle = (record: LifecycleApiRecord): LifecycleInitiative => ({
    actions: (record.actions ?? []).map(normalizeAction),
    archived: record.archived,
    associatedResource: normalizeAssociatedResource(record.subject),
    auditTrail: [],
    comments: [],
    createdAt: record.createdAt,
    description: record.description,
    endAt: record.endAt,
    favorited: record.favorited ?? false,
    id: record.id,
    label: record.name,
    locked: record.locked,
    revision: record.revision,
    startAt: record.startAt,
    status: { name: record.status },
    subject: record.subject?.label,
    taskSummary: record.taskSummary,
    taskCategories: [...(record.categories ?? [])]
        .sort((first, second) => first.position - second.position)
        .map((category) => ({
            color: category.color,
            description: category.description,
            id: category.id,
            label: category.name,
            taskGroups: [...(category.groups ?? [])]
                .sort((first, second) => first.position - second.position)
                .map((group) => ({
                    color: group.color,
                    description: group.description,
                    id: group.id,
                    label: group.name,
                    tasks: (group.tasks ?? []).map((task) => {
                        const participants = task.assignees ?? [];

                        return {
                            ...task,
                            assignees: participants.filter(
                                (participant) => participant.role !== TaskParticipantRole.REVIEWER,
                            ),
                            completed: task.completed ?? task.status?.category === TaskStatusCategory.COMPLETED,
                            dueAt: task.dueAt ? new Date(task.dueAt) : undefined,
                            reviewers: task.reviewers?.length
                                ? task.reviewers
                                : participants.filter(
                                      (participant) => participant.role === TaskParticipantRole.REVIEWER,
                                  ),
                            updatedAt: task.updatedAt ? new Date(task.updatedAt) : undefined,
                        };
                    }),
                })),
        })),
    templateId: record.templateKey,
    updatedAt: record.updatedAt,
    visibility: record.visibility,
});

export const lifecycleService = {
    list: async (params?: { cursor?: string; limit?: number; search?: string; status?: string }) => {
        const page = await apiClient.get<LifecycleListPage, typeof params>('/api/lifecycles', { params });
        return { ...page, items: page.items.map(normalizeLifecycle) };
    },
    getById: async (id: string) => normalizeLifecycle(await apiClient.get<LifecycleApiRecord>(`/api/lifecycles/${id}`)),
    create: async (input: CreateLifecycleInput) =>
        normalizeLifecycle(await apiClient.post<LifecycleApiRecord, CreateLifecycleInput>('/api/lifecycles', input)),
    update: async (id: string, input: UpdateLifecycleInput) =>
        normalizeLifecycle(
            await apiClient.patch<LifecycleApiRecord, UpdateLifecycleInput>(`/api/lifecycles/${id}`, input),
        ),
    updateHierarchyOrder: async (id: string, input: UpdateLifecycleHierarchyOrderInput) =>
        normalizeLifecycle(
            await apiClient.patch<LifecycleApiRecord, UpdateLifecycleHierarchyOrderInput>(
                `/api/lifecycles/${id}/hierarchy/order`,
                input,
            ),
        ),
    delete: (id: string) => apiClient.delete<void>(`/api/lifecycles/${id}`),
    setFavorite: (id: string, favorited: boolean) =>
        favoriteService.set<LifecycleInitiative>('lifecycles', id, favorited),
    collaborators: {
        list: (lifecycleId: string) =>
            apiClient.get<LifecycleCollaboratorRecord[]>(`/api/lifecycles/${lifecycleId}/collaborators`),
        create: (lifecycleId: string, input: LifecycleCollaboratorInput) =>
            apiClient.post<LifecycleCollaboratorRecord, LifecycleCollaboratorInput>(
                `/api/lifecycles/${lifecycleId}/collaborators`,
                input,
            ),
        update: (lifecycleId: string, collaboratorId: string, input: LifecycleCollaboratorUpdateInput) =>
            apiClient.patch<LifecycleCollaboratorRecord, LifecycleCollaboratorUpdateInput>(
                `/api/lifecycles/${lifecycleId}/collaborators/${collaboratorId}`,
                input,
            ),
        delete: (lifecycleId: string, collaboratorId: string) =>
            apiClient.delete<void>(`/api/lifecycles/${lifecycleId}/collaborators/${collaboratorId}`),
    },
    actions: {
        list: (lifecycleId: string) => apiClient.get<LifecycleApiAction[]>(`/api/lifecycles/${lifecycleId}/actions`),
        create: (lifecycleId: string, input: Omit<LifecycleApiAction, 'id' | 'revision'>) =>
            apiClient.post<LifecycleApiAction, Omit<LifecycleApiAction, 'id' | 'revision'>>(
                `/api/lifecycles/${lifecycleId}/actions`,
                input,
            ),
        update: (lifecycleId: string, actionId: string, input: Partial<LifecycleApiAction> & { revision: number }) =>
            apiClient.patch<LifecycleApiAction, Partial<LifecycleApiAction> & { revision: number }>(
                `/api/lifecycles/${lifecycleId}/actions/${actionId}`,
                input,
            ),
        delete: (lifecycleId: string, actionId: string) =>
            apiClient.delete<void>(`/api/lifecycles/${lifecycleId}/actions/${actionId}`),
        execute: (lifecycleId: string, actionId: string, triggerSnapshot: Record<string, unknown> = {}) =>
            apiClient.post<LifecycleActionExecutionRecord, { triggerSnapshot: Record<string, unknown> }>(
                `/api/lifecycles/${lifecycleId}/actions/${actionId}/execute`,
                { triggerSnapshot },
                { headers: { 'Idempotency-Key': crypto.randomUUID() } },
            ),
        executions: (lifecycleId: string, actionId: string) =>
            apiClient.get<LifecycleActionExecutionRecord[]>(
                `/api/lifecycles/${lifecycleId}/actions/${actionId}/executions`,
            ),
    },
};
